<template>
    <div class="company-form-modal">
        <validation-observer ref="companyForm" v-slot="{ invalid }">
            <am-modal :show="show" :title="modalTitle" size="lg" ok-text="Save" :loading="saving"
                :ok-disabled="invalid" @onClose="$emit('close')" @onOk="saveCompany">
                <b-form @submit.prevent="saveCompany">
                    <!-- Error Display -->
                    <error-display :error="error" />

                    <!-- Basic Information -->
                    <h6 class="mb-3 text-primary">Basic Information</h6>
                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.name" name="name" label="Company Name" rules="required"
                                placeholder="Enter company name" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.email" name="email" label="Email" rules="email" type="email"
                                placeholder="Enter company email" />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.registration_no" name="registration_no" label="Registration Number"
                                rules="required" placeholder="Enter registration number" />
                        </b-col>
                        <b-col md="6">
                            <am-select v-model="form.company_classification_id" :options="classifications"
                                rules="required"
                                name="company_classification_id" label="Company Classification" label-name="name"
                                value-name="id" placeholder="Select classification" />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <am-date-picker v-model="form.date_established" name="date_established"
                                label="Date Established" rules="required|no_future_date" />
                        </b-col>
                        <b-col md="6">
                            <am-date-picker v-model="form.date_registered" name="date_registered"
                                label="Date Registered" rules="no_future_date" />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <am-select v-model="form.sustainability_certification"
                                :options="sustainabilityCertificationOptions" name="sustainability_certification"
                                label="Sustainability Certification" label-name="text" value-name="value"
                                placeholder="Select certification" />
                        </b-col>
                        <b-col md="6" v-if="showOtherSustainabilityInput">
                            <am-input v-model="form.other_sustainability_certification"
                                name="other_sustainability_certification" label="Specify Other Certification"
                                rules="required" placeholder="Enter other certification" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.website" name="website" label="Website"
                                placeholder="Enter website URL" />
                        </b-col>
                    </b-row>

                    <!-- Contact Information -->
                    <h6 class="mb-3 text-primary mt-4">Contact Information</h6>
                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.contact_name" name="contact_name" label="Contact Name"
                                rules="required" placeholder="Enter contact name" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.contact_phone" name="contact_phone" label="Contact Phone"
                                rules="required" placeholder="Enter contact phone" />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.secondary_contact" name="secondary_contact"
                                label="Secondary Contact" rules="required" placeholder="Enter secondary contact name" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.secondary_contact_phone" name="secondary_contact_phone"
                                label="Secondary Contact Phone" rules="required"
                                placeholder="Enter secondary contact phone" />
                        </b-col>
                    </b-row>

                    <!-- Location Information -->
                    <h6 class="mb-3 text-primary mt-4">Location Information</h6>
                    <b-row>
                        <b-col md="6">
                            <am-select v-model="form.country_id" :options="countries" name="country_id" label="Country"
                                rules="required" label-name="name" value-name="id" placeholder="Select country"
                                @input="onCountryChange" />
                        </b-col>
                        <b-col md="6">
                            <am-select v-model="form.region_id" :options="regions" name="region_id" label="Region"
                                rules="required" label-name="name" value-name="id" :disabled="!form.country_id"
                                placeholder="Select region" />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.district" name="district" label="District" rules="required"
                                placeholder="Enter district" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.community" name="community" label="Community"
                                placeholder="Enter community" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.location" name="location" label="Location" rules="required"
                                placeholder="Enter location" />
                        </b-col>
                    </b-row>

                    <am-text-area v-model="form.address" name="address" label="Address" rules="required"
                        placeholder="Enter company address" rows="3" />

                   

                    <!-- Membership Information -->
                    <h6 class="mb-3 text-primary mt-4">Membership Information</h6>
                    <b-row>
                        <b-col md="4">
                            <am-input v-model="form.no_of_societies" name="no_of_societies" label="Number of Societies"
                                type="number" min="0" placeholder="Enter number of societies" />
                        </b-col>
                        <b-col md="4">
                            <am-input v-model="form.no_of_members" name="no_of_members" label="Total Members"
                                type="number" min="0" placeholder="Enter total members" />
                        </b-col>
                        <b-col md="4">
                            <am-input v-model="form.no_of_youth" name="no_of_youth" label="Youth Members (17-35)"
                                type="number" min="0" placeholder="Enter youth members" />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.no_of_males" name="no_of_males" label="Male Members" type="number"
                                min="0" placeholder="Enter male members" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.no_of_females" name="no_of_females" label="Female Members"
                                type="number" min="0" placeholder="Enter female members" />
                        </b-col>
                    </b-row>

                    <!-- Business Information -->
                    <h6 class="mb-3 text-primary mt-4">Business Information</h6>
                    <b-row>
                        <b-col md="6">
                            <am-select v-model="form.annual_revenue" :options="annualRevenueOptions"
                                name="annual_revenue" label="Annual Revenue" label-name="text" value-name="value"
                                rules="required" placeholder="Select annual revenue" />
                        </b-col>
                        <b-col md="6">
                            <am-select v-model="form.funding_source" :options="fundingSourceOptions"
                                name="funding_source" label="Funding Source" label-name="text" value-name="value"
                                placeholder="Select funding source" />
                        </b-col>
                        <b-col md="6">
                            <am-select v-model="form.by_laws" :options="byLawsOptions"
                                name="by_laws" label="By-Laws" label-name="text" value-name="value"
                                rules="required" placeholder="Select" />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6">
                            <am-input v-model="form.partner_organization" name="partner_organization"
                                label="Partner Organization" placeholder="Enter partner organization" />
                        </b-col>
                        <b-col md="6">
                            <am-input v-model="form.vat" name="vat" label="VAT Number" placeholder="Enter VAT number" />
                        </b-col>
                    </b-row>

                    <!-- Banking Information -->
                    <h6 class="mb-3 text-primary mt-4">Banking Information</h6>
                    <b-row>
                        <b-col md="4">
                            <am-input v-model="form.bank_name" name="bank_name" label="Bank Name"
                                placeholder="Enter bank name" />
                        </b-col>
                        <b-col md="4">
                            <am-input v-model="form.bank_account_no" name="bank_account_no" label="Account Number"
                                placeholder="Enter account number" />
                        </b-col>
                        <b-col md="4">
                            <am-input v-model="form.branch_name" name="branch_name" label="Branch Name"
                                placeholder="Enter branch name" />
                        </b-col>
                    </b-row>

                    <!-- Notes -->
                    <h6 class="mb-3 text-primary mt-4">Additional Information</h6>
                    <am-text-area v-model="form.notes" name="notes" label="Notes"
                        placeholder="Enter any additional notes" rows="3" />

                    <!-- Status -->
                    <b-form-group>
                        <b-form-checkbox id="is-active" v-model="form.is_active" name="is-active">
                            Company is active
                        </b-form-checkbox>
                    </b-form-group>
                </b-form>
            </am-modal>
        </validation-observer>
    </div>
</template>

<script>
import {
    BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormCheckbox, BFormInvalidFeedback, BRow, BCol,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import showToast from '@/farmbusiness/mixins/showToast'
import AmInput from '@/farmbusiness/components/AmInput'
import AmSelect from '@/farmbusiness/components/AmSelect'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker'
import AmTextArea from '@/farmbusiness/components/AmTextArea'
import AmModal from '@/farmbusiness/components/AmModal'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay'

export default {
    components: {
        BForm,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BFormTextarea,
        BFormCheckbox,
        BFormInvalidFeedback,
        BRow,
        BCol,
        ValidationObserver,
        AmInput,
        AmSelect,
        AmDatePicker,
        AmTextArea,
        AmModal,
        ErrorDisplay,
    },
    mixins: [showToast],
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        company: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            saving: false,
            error: {},
            form: {
                name: null,
                email: null,
                contact_name: null,
                contact_phone: null,
                secondary_contact: null,
                secondary_contact_phone: null,
                country_id: null,
                region_id: null,
                district: null,
                community: null,
                postal_code: null,
                address: null,
                registration_no: null,
                date_established: null,
                date_registered: null,
                location: null,
                annual_revenue: null,
                partner_organization: null,
                company_classification_id: null,
                no_of_societies: null,
                no_of_members: null,
                no_of_males: null,
                no_of_females: null,
                no_of_youth: null,
                sustainability_certification: null,
                other_sustainability_certification: null,
                funding_source: null,
                by_laws: false,
                vat: null,
                website: null,
                bank_name: null,
                bank_account_no: null,
                branch_name: null,
                notes: null,
                is_active: true,
            },
            sustainabilityCertificationOptions: [
                { value: 'Fairtrade', text: 'Fairtrade' },
                { value: 'ISO 22000 & HACCP', text: 'ISO 22000 & HACCP' },
                { value: 'Cocoa life', text: 'Cocoa life' },
                { value: 'Organic certificate', text: 'Organic certificate' },
                { value: 'Rainforest alliance (RA)', text: 'Rainforest alliance (RA)' },
                { value: 'Mondelez international', text: 'Mondelez international' },
                { value: 'GHAFFAP', text: 'GHAFFAP' },
                { value: 'Other', text: 'Other' }
            ],
            fundingSourceOptions: [
                { value: 'Loans', text: 'Loans' },
                { value: 'Grants', text: 'Grants' },
                { value: 'Donations', text: 'Donations' },
                { value: 'Member contributions', text: 'Member contributions' },
                { value: 'Cooperative owned business', text: 'Cooperative owned business' }
            ],
            byLawsOptions: [
                { value: true, text: 'Yes' },
                { value: false, text: 'No' },
            ],
            annualRevenueOptions: [
                { value: '<10k', text: '<10k' },
                { value: '>10k but <50k', text: '>10k but <50k' },
                { value: '>50k <100k', text: '>50k <100k' },
                { value: '>100k', text: '>100k' }
            ],
        }
    },
    computed: {
        countries() {
            return this.$store.getters['auth/countries']
        },
        regions() {
            return this.$store.getters['auth/regions'][this.form.country_id] || []
        },
        classifications() {
            return this.$store.getters['auth/classifications']
        },
        showOtherSustainabilityInput() {
            return this.form.sustainability_certification === 'Other';
        },
        isEditing() {
            return !!this.company;
        },
        modalTitle() {
            return this.isEditing ? 'Edit Company' : 'Add New Company';
        },
    },
    watch: {
        show(newVal) {
            if (newVal) {
                if (this.isEditing && this.company) {
                    this.populateForm();
                }else{
                    this.resetForm();
                }
            }
        },
        'form.sustainability_certification'(newValue) {
            if (newValue !== 'Other') {
                this.form.other_sustainability_certification = '';
            }
        },
    },
    methods: {
        async loadRegions(countryId) {
            try {
                await this.$store.dispatch('auth/getRegions', countryId)
            } catch (error) {
                this.showError('Failed to load regions')
            }
        },

        async onCountryChange() {
            this.form.region_id = null
            if (this.form.country_id) {
                // Check if regions for this country are already loaded
                const existingRegions = this.$store.getters['auth/regions'][this.form.country_id]
                if (!existingRegions || existingRegions.length === 0) {
                    await this.loadRegions(this.form.country_id)
                }
            }
        },
        async saveCompany() {
            // Validate form before proceeding
            const isValid = await this.$refs.companyForm.validate()
            if (!isValid) {
                this.showError('Please fix the errors in the form.')
                return
            }

            this.saving = true
            this.error = {}

            // Client-side validation for "Other" certification
            if (this.form.sustainability_certification === 'Other' && !this.form.other_sustainability_certification) {
                this.error = { detail: 'Please specify the other certification' }
                this.saving = false
                return
            }

            try {
                if (this.isEditing) {
                    await this.$store.dispatch('auth/updateCompany', {
                        companyId: this.company.id,
                        data: this.form
                    })
                    this.showSuccess('Company updated successfully')
                    this.$emit('company-updated')
                } else {
                    await this.$store.dispatch('auth/createCompany', this.form)
                    this.showSuccess('Company created successfully')
                    this.$emit('company-created')
                }
                this.$emit('close')
                this.resetForm()
            } catch (error) {
                this.error = error
                this.showError(this.isEditing ? 'Failed to update company' : 'Failed to create company')
                // Don't hide modal on error - let user fix and retry
            } finally {
                this.saving = false
            }
        },
        populateForm() {
            if (!this.company) return;
            
            this.form = {
                name: this.company.name || '',
                email: this.company.email || '',
                contact_name: this.company.contact_name || '',
                contact_phone: this.company.contact_phone || '',
                secondary_contact: this.company.secondary_contact || '',
                secondary_contact_phone: this.company.secondary_contact_phone || '',
                country_id: this.company.country || null,
            
                district: this.company.district || '',
                community: this.company.community || '',
                postal_code: this.company.postal_code || '',
                address: this.company.address || '',
                registration_no: this.company.registration_no || '',
                date_established: this.company.date_established || '',
                date_registered: this.company.date_registered || '',
                location: this.company.location || '',
                annual_revenue: this.company.annual_revenue || '',
                partner_organization: this.company.partner_organization || '',
                company_classification_id: this.company.company_classification_id || null,
                no_of_societies: this.company.no_of_societies || '',
                no_of_members: this.company.no_of_members || '',
                no_of_males: this.company.no_of_males || '',
                no_of_females: this.company.no_of_females || '',
                no_of_youth: this.company.no_of_youth || '',
                sustainability_certification: this.company.sustainability_certification || null,
                other_sustainability_certification: this.company.other_sustainability_certification || '',
                funding_source: this.company.funding_source || null,
                by_laws: this.company.by_laws === true || this.company.by_laws === 1,
                vat: this.company.vat || '',
                website: this.company.website || '',
                bank_name: this.company.bank_name || '',
                bank_account_no: this.company.bank_account_no || '',
                branch_name: this.company.branch_name || '',
                notes: this.company.notes || '',
                is_active: this.company.is_active !== undefined ? this.company.is_active : true,
            }
            
            // Load regions if country is set
            if (this.form.country_id) {
                this.onCountryChange();
            }
            this.form.region_id = this.company.region || null;
        },
        resetForm() {
            this.form = {
                name: null,
                email: null,
                contact_name: null,
                contact_phone: null,
                secondary_contact: null,
                secondary_contact_phone: null,
                country_id: null,
                region_id: null,
                district: null,
                community: null,
                postal_code: null,
                address: null,
                registration_no: null,
                date_established: null,
                date_registered: null,
                location: null,
                annual_revenue: null,
                partner_organization: null,
                company_classification_id: null,
                no_of_societies: null,
                no_of_members: null,
                no_of_males: null,
                no_of_females: null,
                no_of_youth: null,
                sustainability_certification: null,
                other_sustainability_certification: null,
                funding_source: null,
                by_laws: false,
                vat: null,
                website: null,
                bank_name: null,
                bank_account_no: null,
                branch_name: null,
                notes: null,
                is_active: true,
            }
            this.error = {}
        },

    },
}
</script>